// TMOS Issue Detail Page
// Date: November 15, 2025
// Purpose: Detailed view of an issue with all management features

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeftIcon,
  ExclamationTriangleIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  XCircleIcon,
  PencilIcon,
  UserPlusIcon,
  ArrowUpIcon,
  DocumentTextIcon,
  PlayIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { issueService, Issue, UpdateIssueRequest } from '@/services/issueService';
import { useLanguage } from '@/state/language/LanguageProvider';
import { useAuth } from '@/state/auth/AuthProvider';
import { toast } from 'react-hot-toast';
import api from '@/services/api';

interface User {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
}

export const IssueDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user: currentUser } = useAuth();
  const [issue, setIssue] = useState<Issue | null>(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<User[]>([]);
  const [categories, setCategories] = useState<Array<{ id: string; name: string }>>([]);
  
  // Modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showEscalateModal, setShowEscalateModal] = useState(false);
  const [showInternalNotesModal, setShowInternalNotesModal] = useState(false);
  
  // Form states
  const [editFormData, setEditFormData] = useState<UpdateIssueRequest>({});
  const [selectedUserId, setSelectedUserId] = useState('');
  const [escalateUserId, setEscalateUserId] = useState('');
  const [escalationPrivacy, setEscalationPrivacy] = useState<'PRIVATE' | 'PUBLIC'>('PRIVATE');
  const [escalationMessage, setEscalationMessage] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (id) {
      loadIssue();
      checkAdminStatus();
      loadUsers();
      loadCategories();
    }
  }, [id]);

  const loadCategories = async () => {
    try {
      const response = await api.get('/generic/issue_categories?page=1&limit=1000');
      setCategories(response.data.data || []);
    } catch (error) {
      console.error('Error loading issue categories:', error);
    }
  };

  const checkAdminStatus = async () => {
    try {
      // Check if user is admin by checking their roles
      const token = localStorage.getItem('auth_token');
      if (!token) return;
      
      // Simple check - in real implementation, check roles from API
      // const payload = JSON.parse(atob(token.split('.')[1]));
      setIsAdmin(true); // For now, assume admin if authenticated
    } catch (error) {
      console.error('Error checking admin status:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await api.get('/generic/users');
      setUsers(response.data.data || []);
    } catch (error) {
      console.error('Error loading users:', error);
      // Try alternative endpoint
      try {
        const response = await api.get('/users/admin/users');
        setUsers(response.data.data || response.data || []);
      } catch (err) {
        console.error('Error loading users from admin endpoint:', err);
      }
    }
  };

  const loadIssue = async () => {
    try {
      setLoading(true);
      const data = await issueService.getIssueById(id!);
      setIssue(data);
      setEditFormData({
        title: data.title,
        description: data.description,
        issue_category_id: data.issue_category_id || undefined,
        urgency: data.urgency,
        impact: data.impact,
        privacy: data.privacy,
        status: data.status
      });
      
      // Load internal notes if admin/owner
      if (isAdmin || data.created_by === currentUser?.id) {
        try {
          const notesData = await issueService.getInternalNotes(id!);
          setInternalNotes(notesData.internal_notes || '');
        } catch (error) {
          // Internal notes might not be accessible, that's okay
        }
      }
    } catch (error: any) {
      console.error('Error loading issue:', error);
      toast.error(error.response?.data?.message || 'Failed to load issue');
      navigate('/issues');
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async () => {
    if (!id) return;
    try {
      await issueService.resolveIssue(id);
      toast.success(t('issues.resolvedSuccess') || 'Issue resolved successfully');
      loadIssue();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to resolve issue');
    }
  };

  const handleReopen = async () => {
    if (!id) return;
    try {
      await issueService.reopenIssue(id);
      toast.success(t('issues.reopenedSuccess') || 'Issue reopened successfully');
      loadIssue();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to reopen issue');
    }
  };

  const handleSetInProgress = async () => {
    if (!id) return;
    try {
      await issueService.setIssueInProgress(id);
      toast.success(t('issues.setInProgressSuccess') || 'Issue set to in progress');
      loadIssue();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update issue status');
    }
  };

  const handleClose = async () => {
    if (!id) return;
    try {
      await issueService.closeIssue(id);
      toast.success(t('issues.closedSuccess') || 'Issue closed successfully');
      loadIssue();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to close issue');
    }
  };

  const handleUpdateIssue = async () => {
    if (!id) return;
    try {
      await issueService.updateIssue(id, editFormData);
      toast.success(t('issues.updatedSuccess') || 'Issue updated successfully');
      setShowEditModal(false);
      loadIssue();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update issue');
    }
  };

  const handleAssign = async () => {
    if (!id || !selectedUserId) return;
    try {
      await issueService.assignIssue(id, selectedUserId);
      toast.success(t('issues.assignedSuccess') || 'Issue assigned successfully');
      setShowAssignModal(false);
      setSelectedUserId('');
      loadIssue();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to assign issue');
    }
  };

  const handleEscalate = async () => {
    if (!id || !escalateUserId) return;
    try {
      await issueService.escalateIssue(id, escalateUserId, escalationPrivacy, escalationMessage);
      toast.success(t('issues.escalatedSuccess') || 'Issue escalated successfully');
      setShowEscalateModal(false);
      setEscalateUserId('');
      setEscalationPrivacy('PRIVATE');
      setEscalationMessage('');
      loadIssue();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to escalate issue');
    }
  };

  const handleSaveInternalNotes = async () => {
    if (!id) return;
    try {
      await issueService.updateIssue(id, { internal_notes: internalNotes });
      toast.success(t('issues.internalNotesSaved') || 'Internal notes saved successfully');
      setShowInternalNotesModal(false);
      loadIssue();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to save internal notes');
    }
  };

  const canEdit = () => {
    if (!issue || !currentUser) return false;
    return isAdmin || issue.created_by === currentUser.id;
  };

  const canManage = () => {
    return isAdmin; // Only admins can assign, escalate, manage status
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--color-surface-bg)] flex items-center justify-center">
        <p className="text-[var(--color-secondary-text)]">{t('common.loading') || 'Loading...'}</p>
      </div>
    );
  }

  if (!issue) {
    return (
      <div className="min-h-screen bg-[var(--color-surface-bg)] flex items-center justify-center">
        <Card variant="outline" padding="lg">
          <CardContent className="text-center">
            <p className="text-[var(--color-secondary-text)] mb-4">
              {t('issues.notFound') || 'Issue not found'}
            </p>
            <Button onClick={() => navigate('/issues')}>
              {t('issues.backToIssues') || 'Back to Issues'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'OPEN': return 'bg-[var(--color-info)]/10 text-[var(--color-info)]';
      case 'IN_PROGRESS': return 'bg-[var(--color-warning)]/10 text-[var(--color-warning)]';
      case 'RESOLVED': return 'bg-[var(--color-success)]/10 text-[var(--color-success)]';
      case 'CLOSED': return 'bg-[var(--color-border)] text-[var(--color-secondary-text)]';
      default: return 'bg-[var(--color-border)] text-[var(--color-secondary-text)]';
    }
  };

  const getUrgencyBadgeColor = (urgency: string) => {
    switch (urgency) {
      case 'CRITICAL': return 'bg-[var(--color-error)]/10 text-[var(--color-error)]';
      case 'HIGH': return 'bg-[var(--color-warning)]/10 text-[var(--color-warning)]';
      case 'MEDIUM': return 'bg-[var(--color-warning)]/10 text-[var(--color-warning)]';
      case 'LOW': return 'bg-[var(--color-success)]/10 text-[var(--color-success)]';
      default: return 'bg-[var(--color-border)] text-[var(--color-secondary-text)]';
    }
  };

  return (
    <div className="min-h-screen bg-[var(--color-surface-bg)]">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/issues')}
            className="mb-4"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            {t('common.back') || 'Back'}
          </Button>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <ExclamationTriangleIcon className="w-8 h-8 text-[var(--color-primary-bg)]" />
                <h1 className="text-3xl font-bold text-[var(--color-surface-text)]">
                  {issue.title}
                </h1>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeColor(issue.status)}`}>
                  {issue.status}
                </span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getUrgencyBadgeColor(issue.urgency)}`}>
                  {t('issues.urgency') || 'Urgency'}: {issue.urgency}
                </span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getUrgencyBadgeColor(issue.impact)}`}>
                  {t('issues.impact') || 'Impact'}: {issue.impact}
                </span>
                {issue.privacy === 'PUBLIC' && (
                  <span className="px-3 py-1 rounded-full text-sm font-medium bg-[var(--color-info)]/10 text-[var(--color-info)]">
                    {t('issues.public') || 'Public'}
                  </span>
                )}
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              {canEdit() && (
                <Button onClick={() => setShowEditModal(true)} variant="outline">
                  <PencilIcon className="w-5 h-5 mr-2" />
                  {t('common.edit') || 'Edit'}
                </Button>
              )}
              {canManage() && (
                <>
                  <Button onClick={() => setShowAssignModal(true)} variant="outline">
                    <UserPlusIcon className="w-5 h-5 mr-2" />
                    {t('issues.assign') || 'Assign'}
                  </Button>
                  <Button onClick={() => setShowEscalateModal(true)} variant="outline">
                    <ArrowUpIcon className="w-5 h-5 mr-2" />
                    {t('issues.escalate') || 'Escalate'}
                  </Button>
                  {canEdit() && (
                    <Button onClick={() => setShowInternalNotesModal(true)} variant="outline">
                      <DocumentTextIcon className="w-5 h-5 mr-2" />
                      {t('issues.internalNotes') || 'Internal Notes'}
                    </Button>
                  )}
                </>
              )}
              {canManage() && issue.status === 'OPEN' && (
                <Button onClick={handleSetInProgress} variant="outline">
                  <PlayIcon className="w-5 h-5 mr-2" />
                  {t('issues.setInProgress') || 'Set In Progress'}
                </Button>
              )}
              {canManage() && issue.status !== 'RESOLVED' && issue.status !== 'CLOSED' && (
                <Button onClick={handleResolve} variant="success">
                  <CheckCircleIcon className="w-5 h-5 mr-2" />
                  {t('issues.resolve') || 'Resolve'}
                </Button>
              )}
              {canManage() && issue.status === 'RESOLVED' && (
                <Button onClick={handleClose} variant="outline">
                  <XMarkIcon className="w-5 h-5 mr-2" />
                  {t('issues.close') || 'Close'}
                </Button>
              )}
              {issue.status === 'CLOSED' && (
                <Button onClick={handleReopen} variant="outline">
                  <XCircleIcon className="w-5 h-5 mr-2" />
                  {t('issues.reopen') || 'Reopen'}
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Description */}
            <Card variant="outline" padding="md">
              <CardHeader>
                <CardTitle>{t('issues.description') || 'Description'}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-[var(--color-surface-text)] whitespace-pre-wrap">
                  {issue.description}
                </p>
              </CardContent>
            </Card>

            {/* Discussion Thread */}
            {issue.discussion_id && (
              <Card variant="outline" padding="md">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ChatBubbleLeftRightIcon className="w-5 h-5" />
                    {t('issues.discussion') || 'Discussion'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-[var(--color-secondary-text)]">
                    {t('issues.discussionPlaceholder') || 'Discussion thread will be displayed here'}
                  </p>
                  {/* TODO: Integrate discussion component */}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Details */}
            <Card variant="outline" padding="md">
              <CardHeader>
                <CardTitle>{t('issues.details') || 'Details'}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {issue.category_name && (
                  <div>
                    <label className="text-sm font-medium text-[var(--color-secondary-text)]">
                      {t('issues.category') || 'Category'}
                    </label>
                    <p className="text-[var(--color-surface-text)]">{issue.category_name}</p>
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium text-[var(--color-secondary-text)]">
                    {t('issues.targetType') || 'Target Type'}
                  </label>
                  <p className="text-[var(--color-surface-text)]">{issue.target_type}</p>
                </div>
                {issue.creator_name && (
                  <div>
                    <label className="text-sm font-medium text-[var(--color-secondary-text)]">
                      {t('issues.createdBy') || 'Created By'}
                    </label>
                    <p className="text-[var(--color-surface-text)]">{issue.creator_name}</p>
                  </div>
                )}
                {issue.assigned_to_name && (
                  <div>
                    <label className="text-sm font-medium text-[var(--color-secondary-text)]">
                      {t('issues.assignedTo') || 'Assigned To'}
                    </label>
                    <p className="text-[var(--color-surface-text)]">{issue.assigned_to_name}</p>
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium text-[var(--color-secondary-text)]">
                    {t('issues.createdAt') || 'Created At'}
                  </label>
                  <p className="text-[var(--color-surface-text)]">
                    {new Date(issue.created_at).toLocaleString()}
                  </p>
                </div>
                {issue.resolved_at && (
                  <div>
                    <label className="text-sm font-medium text-[var(--color-secondary-text)]">
                      {t('issues.resolvedAt') || 'Resolved At'}
                    </label>
                    <p className="text-[var(--color-surface-text)]">
                      {new Date(issue.resolved_at).toLocaleString()}
                    </p>
                  </div>
                )}
                {issue.closed_at && (
                  <div>
                    <label className="text-sm font-medium text-[var(--color-secondary-text)]">
                      {t('issues.closedAt') || 'Closed At'}
                    </label>
                    <p className="text-[var(--color-surface-text)]">
                      {new Date(issue.closed_at).toLocaleString()}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Edit Modal */}
        <Modal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          title={t('issues.editIssue') || 'Edit Issue'}
          size="lg"
        >
          <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                    {t('issues.title') || 'Title'} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={editFormData.title || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                    className="w-full px-4 py-2 border rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] border-[var(--color-border)]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                    {t('issues.description') || 'Description'} <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    required
                    rows={6}
                    value={editFormData.description || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                    className="w-full px-4 py-2 border rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] border-[var(--color-border)]"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                      {t('issues.category') || 'Category'}
                    </label>
                    <select
                      value={editFormData.issue_category_id || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, issue_category_id: e.target.value || undefined })}
                      className="w-full px-4 py-2 border rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] border-[var(--color-border)]"
                    >
                      <option value="">{t('issues.noCategory') || 'No Category'}</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                      {t('issues.urgency') || 'Urgency'}
                    </label>
                    <select
                      value={editFormData.urgency || 'MEDIUM'}
                      onChange={(e) => setEditFormData({ ...editFormData, urgency: e.target.value as any })}
                      className="w-full px-4 py-2 border rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] border-[var(--color-border)]"
                    >
                      <option value="LOW">{t('issues.low') || 'Low'}</option>
                      <option value="MEDIUM">{t('issues.medium') || 'Medium'}</option>
                      <option value="HIGH">{t('issues.high') || 'High'}</option>
                      <option value="CRITICAL">{t('issues.critical') || 'Critical'}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                      {t('issues.impact') || 'Impact'}
                    </label>
                    <select
                      value={editFormData.impact || 'MEDIUM'}
                      onChange={(e) => setEditFormData({ ...editFormData, impact: e.target.value as any })}
                      className="w-full px-4 py-2 border rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] border-[var(--color-border)]"
                    >
                      <option value="LOW">{t('issues.low') || 'Low'}</option>
                      <option value="MEDIUM">{t('issues.medium') || 'Medium'}</option>
                      <option value="HIGH">{t('issues.high') || 'High'}</option>
                      <option value="CRITICAL">{t('issues.critical') || 'Critical'}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                      {t('issues.privacy') || 'Privacy'}
                    </label>
                    <select
                      value={editFormData.privacy || 'PRIVATE'}
                      onChange={(e) => setEditFormData({ ...editFormData, privacy: e.target.value as any })}
                      className="w-full px-4 py-2 border rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] border-[var(--color-border)]"
                    >
                      <option value="PRIVATE">{t('issues.private') || 'Private'}</option>
                      <option value="PUBLIC">{t('issues.public') || 'Public'}</option>
                    </select>
                  </div>
                </div>
                {canManage() && (
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                      {t('issues.status') || 'Status'}
                    </label>
                    <select
                      value={editFormData.status || issue.status}
                      onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value as any })}
                      className="w-full px-4 py-2 border rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] border-[var(--color-border)]"
                    >
                      <option value="OPEN">{t('issues.open') || 'Open'}</option>
                      <option value="IN_PROGRESS">{t('issues.inProgress') || 'In Progress'}</option>
                      <option value="RESOLVED">{t('issues.resolved') || 'Resolved'}</option>
                      <option value="CLOSED">{t('issues.closed') || 'Closed'}</option>
                    </select>
                  </div>
                )}
            <div className="flex justify-end gap-4 pt-4">
              <Button variant="outline" onClick={() => setShowEditModal(false)}>
                {t('common.cancel') || 'Cancel'}
              </Button>
              <Button onClick={handleUpdateIssue} className="bg-[var(--color-primary-bg)] text-[var(--color-primary-text)]">
                {t('common.save') || 'Save'}
              </Button>
            </div>
          </div>
        </Modal>

        {/* Assign Modal */}
        {showAssignModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <Card variant="outline" padding="lg" className="max-w-md w-full mx-4 bg-[var(--color-surface-bg)]">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>{t('issues.assignIssue') || 'Assign Issue'}</CardTitle>
                  <Button variant="ghost" onClick={() => setShowAssignModal(false)}>
                    <XMarkIcon className="w-5 h-5" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                    {t('issues.assignTo') || 'Assign To'} <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={selectedUserId}
                    onChange={(e) => setSelectedUserId(e.target.value)}
                    className="w-full px-4 py-2 border rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] border-[var(--color-border)]"
                  >
                    <option value="">{t('issues.selectUser') || 'Select a user...'}</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.first_name && user.last_name 
                          ? `${user.first_name} ${user.last_name} (${user.email})`
                          : user.email}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex justify-end gap-4 pt-4">
                  <Button variant="outline" onClick={() => setShowAssignModal(false)}>
                    {t('common.cancel') || 'Cancel'}
                  </Button>
                  <Button 
                    onClick={handleAssign} 
                    disabled={!selectedUserId}
                    className="bg-[var(--color-primary-bg)] text-[var(--color-primary-text)]"
                  >
                    {t('issues.assign') || 'Assign'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Escalate Modal */}
        {showEscalateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <Card variant="outline" padding="lg" className="max-w-md w-full mx-4 bg-[var(--color-surface-bg)]">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>{t('issues.escalateIssue') || 'Escalate Issue'}</CardTitle>
                  <Button variant="ghost" onClick={() => setShowEscalateModal(false)}>
                    <XMarkIcon className="w-5 h-5" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                    {t('issues.escalateTo') || 'Escalate To'} <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={escalateUserId}
                    onChange={(e) => setEscalateUserId(e.target.value)}
                    className="w-full px-4 py-2 border rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] border-[var(--color-border)]"
                  >
                    <option value="">{t('issues.selectUser') || 'Select a user...'}</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.first_name && user.last_name 
                          ? `${user.first_name} ${user.last_name} (${user.email})`
                          : user.email}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                    {t('issues.escalationPrivacy') || 'Escalation Privacy'} <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={escalationPrivacy}
                    onChange={(e) => setEscalationPrivacy(e.target.value as 'PRIVATE' | 'PUBLIC')}
                    className="w-full px-4 py-2 border rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] border-[var(--color-border)]"
                  >
                    <option value="PRIVATE">{t('issues.private') || 'Private'}</option>
                    <option value="PUBLIC">{t('issues.public') || 'Public'}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                    {t('issues.escalationMessage') || 'Escalation Message'} ({t('issues.optional') || 'Optional'})
                  </label>
                  <textarea
                    rows={4}
                    value={escalationMessage}
                    onChange={(e) => setEscalationMessage(e.target.value)}
                    className="w-full px-4 py-2 border rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] border-[var(--color-border)]"
                    placeholder={t('issues.escalationMessagePlaceholder') || 'Add a message explaining why this issue is being escalated...'}
                  />
                </div>
                <div className="flex justify-end gap-4 pt-4">
                  <Button variant="outline" onClick={() => setShowEscalateModal(false)}>
                    {t('common.cancel') || 'Cancel'}
                  </Button>
                  <Button 
                    onClick={handleEscalate} 
                    disabled={!escalateUserId}
                    className="bg-[var(--color-primary-bg)] text-[var(--color-primary-text)]"
                  >
                    {t('issues.escalate') || 'Escalate'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Internal Notes Modal */}
        {showInternalNotesModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <Card variant="outline" padding="lg" className="max-w-2xl w-full mx-4 bg-[var(--color-surface-bg)]">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>{t('issues.internalNotes') || 'Internal Notes'}</CardTitle>
                  <Button variant="ghost" onClick={() => setShowInternalNotesModal(false)}>
                    <XMarkIcon className="w-5 h-5" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-[var(--color-secondary-text)]">
                  {t('issues.internalNotesDescription') || 'Internal notes are only visible to admins and issue owners. They are not visible to the issue creator.'}
                </p>
                <div>
                  <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                    {t('issues.notes') || 'Notes'}
                  </label>
                  <textarea
                    rows={8}
                    value={internalNotes}
                    onChange={(e) => setInternalNotes(e.target.value)}
                    className="w-full px-4 py-2 border rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] border-[var(--color-border)]"
                    placeholder={t('issues.internalNotesPlaceholder') || 'Add internal notes about this issue...'}
                  />
                </div>
                <div className="flex justify-end gap-4 pt-4">
                  <Button variant="outline" onClick={() => setShowInternalNotesModal(false)}>
                    {t('common.cancel') || 'Cancel'}
                  </Button>
                  <Button 
                    onClick={handleSaveInternalNotes}
                    className="bg-[var(--color-primary-bg)] text-[var(--color-primary-text)]"
                  >
                    {t('common.save') || 'Save'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};
